import React, { useState, useEffect } from 'react';

import { AlertRoot, AlertDescription, Text } from '@vimeo/bokeh';
import { translate } from '@vimeo/i18n-runtime';
import { UpgradeBadge } from '@vimeo/monetization-upsells';
import { buildUpgradePlanUrl } from '@vimeo/picox';
import { useViewer } from '@vimeo/viewer';

const WARNING_THRESHOLD_PERCENT = 95;

export default function StorageQuotaWarningBanner() {
  const viewer = useViewer();
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Only show banner if:
    // 1. User has restricted quota data (only returned for repackaged plans)
    // 2. User is approaching the limit
    const restricted = viewer?.user?.uploadQuota?.restricted;
    if (!restricted || !restricted.max) {
      setShowBanner(false);
      return;
    }

    const percentUsed = ((restricted.used ?? 0) / restricted.max) * 100;
    const shouldShow = percentUsed >= WARNING_THRESHOLD_PERCENT;
    setShowBanner(shouldShow);
  }, [viewer?.user?.uploadQuota?.restricted]);

  const upgradeUrl = buildUpgradePlanUrl(
    {
      paywallTrigger: 'storage_quota_warning_upgrade_button',
      paywallLocation: 'storage_quota_banner',
      paywallFeature: 'storage_limit',
    },
    {
      upsell: 'storage_quota_banner',
      integration: 'none',
      feature: 'Storage_at_limit',
      paywall: '1',
      upsellFeatureCategory: 'Storage',
      upsellSpecificFeature: 'Storage_at_limit',
    },
  );

  if (!showBanner) {
    return null;
  }

  return (
    <AlertRoot
      backgroundColor="status-destructive-secondary"
      marginBottom="0"
      gap="sm"
      justifyContent="center"
      p="sm"
      borderRadius="none"
      alignItems="center"
      minH="24px"
    >
      <AlertDescription
        display="flex"
        gap="sm"
        width="100%"
        justifyContent="center"
        alignItems="center"
      >
        <Text variant="body-sm" fontWeight="medium" maxWidth="600px">
          {translate({
            singular: "You've hit the storage limit. Upgrade to keep uploading videos.",
          })}
        </Text>
        <UpgradeBadge
          href={upgradeUrl}
          name="storage_quota_warning_upgrade_button"
          location="storage_quota_banner"
        />
      </AlertDescription>
    </AlertRoot>
  );
}
